import { z } from "zod";
import { instrumentNameSchema, questionnaireAnswerSchema, questionnaireItemSchema } from "./questionnaires.js";
import { readLabResultSchema } from "./labs.js";
/* The summary a client is shown after submitting the questionnaire.

 * The snapshot freezes what can change after submit -- lab draws and
 * check-ins as of submitted_at -- exactly as measured. It stores nothing
 * derived: no differences, no new/dropped sets, no ordering by meaning (root
 * CLAUDE.md "The software does not interpret"). Answers are not in the
 * snapshot at all; they are read live from the frozen submission.
 */
export const QUESTIONNAIRE_SUMMARY_SNAPSHOT_VERSION = 1;
export const quickCheckinSnapshotSchema = z.object({
    id: z.string(),
    createdAt: z.string(),
    energy: z.number().int().min(0).max(100).nullable(),
    mood: z.number().int().min(0).max(100).nullable(),
});
// freeText is deliberately absent: it is never shown.
export const fullCheckinSnapshotSchema = z.object({
    id: z.string(),
    createdAt: z.string(),
    checklistVersion: z.number().int(),
    symptoms: z.array(z.string()),
    noneOfThese: z.boolean(),
});
function baselineSchema(checkin) {
    return z.discriminatedUnion("source", [
        z.object({ source: z.literal("previous_summary"), previousSubmittedAt: z.string(), checkin }),
        z.object({ source: z.literal("previous_checkin"), checkin }),
    ]);
}
export const quickBaselineSchema = baselineSchema(quickCheckinSnapshotSchema);
export const fullBaselineSchema = baselineSchema(fullCheckinSnapshotSchema);
export const summaryCheckinsSchema = z.object({
    quick: z.object({
        latest: quickCheckinSnapshotSchema.nullable(),
        baseline: quickBaselineSchema.nullable(),
    }),
    full: z.object({
        latest: fullCheckinSnapshotSchema.nullable(),
        baseline: fullBaselineSchema.nullable(),
    }),
});
// labResults is newest first.
export const questionnaireSummarySnapshotSchema = z.object({
    labResults: z.array(readLabResultSchema),
    checkins: summaryCheckinsSchema,
});
export const questionnaireSummaryListItemSchema = z.object({
    id: z.string(),
    instrument: instrumentNameSchema,
    submittedAt: z.string(),
});
export const questionnaireSummaryCtaSchema = z.enum(["infusion", "support"]);
export const questionnaireSummarySchema = z.object({
    id: z.string(),
    instrument: instrumentNameSchema,
    instrumentVersion: z.number().int().positive(),
    submittedAt: z.string(),
    cta: questionnaireSummaryCtaSchema.nullable(),
    answers: z.array(questionnaireAnswerSchema),
    items: z.array(questionnaireItemSchema),
    labResults: z.array(readLabResultSchema),
    checkins: summaryCheckinsSchema,
});
export const questionnaireSummaryListSchema = z.array(questionnaireSummaryListItemSchema);
